import WarningIcon from '@mui/icons-material/Warning';
import { Grid, styled, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { Slide } from 'interfaces/slide';
import { map, some } from 'lodash';

import { filtersDrawerWidth } from 'components/atoms/FlexContainers/FlexContainers';
import React from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { CardMode, procedureCardBorderRadiusPx } from '..';
import { useCurrentProcedurePageCardsPerRow, useProcedurePageColumns } from '../../useProcedurePageColumns';
import SlideMetadataChipCollection from './SlideMetadataChipCollection';
import SlideThumbnailImage from './SlideThumbnailImage';
import SlideZoomBadge from './SlideZoomBadge';
import { getSlideCarouselTone } from './slideCarouselChrome';
import { defaultSlideCarouselPresentation, SlideCarouselPresentation } from './slideCarouselPresentation';

const mainHorizontalPadding = 64;
export const procedureCardThumbnailFrameWidthPx = 1;
export const getProcedureCardCarouselGapSize = (columns: number) => (columns >= 5 ? 1.5 : 3);

const Label = styled(Box)(({ theme }) => ({
  padding: '3px 10px',
  float: 'left',
  margin: '10px',
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 3,
}));

const QCLabel = styled(Label)(({ theme }) => ({
  backgroundColor: theme.palette.warning.main,
}));

const QALabel = styled(Label)(() => ({
  backgroundColor: '#607188',
}));

const carouselHeightByColumnsAndSideComponents = (columns: number, usedWidth: number, gapSize: number = 3) => {
  const gapSizeInPx = gapSize * 8;
  const gapSizeStep = columns > 1 ? ` - (${columns - 1} * ${gapSizeInPx}px)` : '';
  const availableWidth = usedWidth > 0 ? `(100vw - ${usedWidth}px)` : '100vw';
  const heightCalculation = `calc(${availableWidth} / ${columns}${gapSizeStep})`;
  return {
    height: heightCalculation,
    maxHeight: heightCalculation,
  };
};

const useCarouselItemStyle = () => {
  const theme = useTheme();

  const [advancedSearchDrawerOpen] = useQueryParam('advancedSearchDrawerOpen', BooleanParam);

  const usedWidth = mainHorizontalPadding + (advancedSearchDrawerOpen ? filtersDrawerWidth : 0);

  const { xs, sm, md, lg } = useProcedurePageColumns();

  return {
    flexShrink: 0,
    position: 'relative',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('lg')]: carouselHeightByColumnsAndSideComponents(
      lg,
      usedWidth,
      getProcedureCardCarouselGapSize(lg)
    ),
    [theme.breakpoints.between('md', 'lg')]: carouselHeightByColumnsAndSideComponents(
      md,
      usedWidth,
      getProcedureCardCarouselGapSize(md)
    ),
    [theme.breakpoints.between('sm', 'md')]: carouselHeightByColumnsAndSideComponents(
      sm,
      usedWidth,
      getProcedureCardCarouselGapSize(sm)
    ),
    [theme.breakpoints.down('sm')]: carouselHeightByColumnsAndSideComponents(
      xs,
      usedWidth,
      getProcedureCardCarouselGapSize(xs)
    ),
  };
};

interface Props {
  slide: Slide;
  active?: boolean;
  presentation?: SlideCarouselPresentation;
  mode?: CardMode;
}

export const shouldShowProcedureCardThumbnailFrame = ({
  themeMode,
  slideStainingType,
}: {
  themeMode: 'light' | 'dark';
  slideStainingType?: string | null;
}) => themeMode === 'dark' && getSlideCarouselTone(slideStainingType) === 'multiplex';

const SlideCarouselThumbnailFrame: React.FC<{
  slide: Slide;
  presentation: SlideCarouselPresentation;
}> = ({ slide, presentation }) => {
  const theme = useTheme();
  const shouldShowThumbnailFrame = shouldShowProcedureCardThumbnailFrame({
    themeMode: theme.palette.mode,
    slideStainingType: slide.stainingType,
  });
  const thumbnailFrameRadius = `${procedureCardBorderRadiusPx}px`;
  const topThumbnailContentRadius = shouldShowThumbnailFrame
    ? `calc(${procedureCardBorderRadiusPx}px - ${procedureCardThumbnailFrameWidthPx}px)`
    : `${procedureCardBorderRadiusPx}px`;
  const usesFullThumbnailCorners = presentation.thumbnailCornerMode === 'full';
  const bottomThumbnailContentRadius = usesFullThumbnailCorners ? topThumbnailContentRadius : thumbnailFrameRadius;
  const thumbnailFramePadding = shouldShowThumbnailFrame
    ? usesFullThumbnailCorners
      ? `${procedureCardThumbnailFrameWidthPx}px`
      : `${procedureCardThumbnailFrameWidthPx}px ${procedureCardThumbnailFrameWidthPx}px 0`
    : 0;

  return (
    <Box
      data-testid="carousel-item-thumbnail-frame"
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        ...(usesFullThumbnailCorners
          ? { borderRadius: thumbnailFrameRadius }
          : {
              borderTopLeftRadius: thumbnailFrameRadius,
              borderTopRightRadius: thumbnailFrameRadius,
            }),
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        p: thumbnailFramePadding,
      }}
    >
      <Box
        data-testid="carousel-item-thumbnail-content"
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          ...(usesFullThumbnailCorners
            ? {
                borderTopLeftRadius: topThumbnailContentRadius,
                borderTopRightRadius: topThumbnailContentRadius,
                borderBottomLeftRadius: bottomThumbnailContentRadius,
                borderBottomRightRadius: bottomThumbnailContentRadius,
              }
            : {
                borderTopLeftRadius: topThumbnailContentRadius,
                borderTopRightRadius: topThumbnailContentRadius,
              }),
        }}
      >
        <SlideThumbnailImage slide={slide} />
      </Box>
    </Box>
  );
};

const SlideCarouselItem: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  children,
  slide,
  active,
  presentation = defaultSlideCarouselPresentation,
  mode = 'full',
}) => {
  const [hovered, setHovered] = React.useState(false);

  const carouselItemStyle = useCarouselItemStyle();
  const currentCardsPerRow = useCurrentProcedurePageCardsPerRow();
  const shouldHideMetadataUntilHover =
    presentation.metadataVisibilityStrategy === 'hover-when-dense' && currentCardsPerRow >= 6;
  const shouldEnableHoverPresentation = presentation.metadataVisibilityStrategy === 'hover-when-dense';

  const hasUnusableTag = some(
    map(slide?.slideTagAssignments, 'slideMetadataTag'),
    (tag) => tag?.tagValue === 'Unusable'
  );

  return (
    <Box
      className={`carousel-item${active ? ' active' : ''}`}
      data-testid="carousel-item"
      sx={carouselItemStyle}
      onMouseEnter={shouldEnableHoverPresentation && active ? () => setHovered(true) : undefined}
      onMouseLeave={shouldEnableHoverPresentation && active ? () => setHovered(false) : undefined}
    >
      <SlideCarouselThumbnailFrame slide={slide} presentation={presentation} />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </Box>
      <Grid
        container
        sx={{ height: '100%', width: '100%', position: 'relative' }}
        justifyContent="center"
        alignItems="center"
      >
        {mode === 'full' && (!shouldHideMetadataUntilHover || hovered) && (
          <Grid item>
            <SlideMetadataChipCollection slide={slide} />
          </Grid>
        )}
        {mode === 'full' && (
          <Grid item container direction="column" sx={{ height: '100%' }} justifyContent="flex-end">
            <Grid container item>
              {(slide.qcFailed || hasUnusableTag) && (
                <Grid item>
                  <QCLabel>
                    <WarningIcon />
                    QC
                  </QCLabel>
                </Grid>
              )}

              {some(slide.experimentResults, { qaFailed: true }) && (
                <Grid item>
                  <QALabel className="label qa">QA</QALabel>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {presentation.showZoomIndicator && (
          <Grid item>
            <SlideZoomBadge hovered={active && hovered} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SlideCarouselItem;
